import {
  accountMutationHeaders,
  readBoundedAccountJson,
} from "./account-state-session-repository.js";
import {
  createAccountOperationId,
  isAccountOperationId,
} from "./account-operation-id.js";
import {
  canonicalizeTutorialProgressCommandIds,
  decodeTutorialProgressEnabledState,
} from "../tutorial-progress-contract.js";

const SCHEMA_VERSION = 1;
const RESPONSE_MAX_BYTES = 160 * 1024;
const ACCOUNT_PATH = "/api/v1/account/tutorial-progress";

function isRecord(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function hasExactFields(value, expectedFields) {
  if (!isRecord(value)) return false;
  const actualFields = Object.keys(value).sort();
  const sortedExpected = expectedFields.slice().sort();
  return actualFields.length === sortedExpected.length
    && actualFields.every((field, index) => field === sortedExpected[index]);
}

function decodeProjection(value) {
  if (
    hasExactFields(value, ["kind", "schemaVersion"])
    && value.kind === "disabled"
    && value.schemaVersion === SCHEMA_VERSION
  ) {
    return Object.freeze({ kind: "disabled" });
  }
  if (
    !hasExactFields(value, ["kind", "schemaVersion", "revision", "state"])
    || value.kind !== "enabled"
    || value.schemaVersion !== SCHEMA_VERSION
    || !Number.isSafeInteger(value.revision)
    || value.revision <= 0
  ) {
    throw new TypeError("Account tutorial progress projection is invalid.");
  }
  return Object.freeze({
    kind: "enabled",
    revision: String(value.revision),
    state: decodeTutorialProgressEnabledState(value.state),
  });
}

function decodeAcknowledgement(value, operationId) {
  if (
    !hasExactFields(value, ["kind", "operationId", "authoritativeState"])
    || !["applied", "conflict"].includes(value.kind)
    || value.operationId !== operationId
  ) {
    throw new TypeError("Tutorial progress acknowledgement is invalid.");
  }
  return Object.freeze({
    kind: value.kind,
    authoritativeState: decodeProjection(value.authoritativeState),
  });
}

function accountUrl(apiBaseUrl, suffix = "") {
  let base;
  try {
    base = new URL(String(apiBaseUrl ?? ""));
  } catch (_error) {
    throw new TypeError("Tutorial progress API base URL is invalid.");
  }
  if (
    !["http:", "https:"].includes(base.protocol)
    || base.username
    || base.password
    || base.pathname !== "/"
    || base.search
    || base.hash
  ) {
    throw new TypeError("Tutorial progress API base URL must be an HTTP origin.");
  }
  return `${base.origin}${ACCOUNT_PATH}${suffix}`;
}

function exactCompletedIds(value, { allowEmpty = true } = {}) {
  const decoded = decodeTutorialProgressEnabledState({
    kind: "ready",
    completedIds: value,
  }).completedIds;
  if (!allowEmpty && decoded.length === 0) {
    throw new TypeError("At least one tutorial ID is required.");
  }
  return decoded;
}

function revisionNumber(value) {
  if (!/^[1-9][0-9]*$/.test(value ?? "")) {
    throw new TypeError("Account tutorial revision is invalid.");
  }
  const revision = Number(value);
  if (!Number.isSafeInteger(revision)) {
    throw new TypeError("Account tutorial revision is invalid.");
  }
  return revision;
}

export function createTutorialProgressAccountProvider({
  apiBaseUrl,
  fetchImpl = globalThis.fetch,
  createOperationId = createAccountOperationId,
} = {}) {
  if (typeof fetchImpl !== "function" || typeof createOperationId !== "function") {
    throw new TypeError("Tutorial account provider requires fetch and operation identity.");
  }
  const urls = Object.freeze({
    load: accountUrl(apiBaseUrl),
    enable: accountUrl(apiBaseUrl, "/enable"),
    complete: accountUrl(apiBaseUrl, "/complete"),
    reset: accountUrl(apiBaseUrl, "/reset"),
  });

  function operationId() {
    const value = createOperationId();
    if (!isAccountOperationId(value)) {
      throw new TypeError("Tutorial operation ID is invalid.");
    }
    return value;
  }

  async function load({ signal } = {}) {
    const response = await fetchImpl(urls.load, {
      method: "GET",
      cache: "no-store",
      credentials: "include",
      headers: { Accept: "application/json" },
      redirect: "error",
      signal,
    });
    if (response.status === 401) return Object.freeze({ kind: "signedOut" });
    if (!response.ok) throw new Error("Tutorial progress request failed.");
    return decodeProjection(await readBoundedAccountJson(
      response,
      "Account tutorial progress",
      RESPONSE_MAX_BYTES,
    ));
  }

  async function post(session, name, command, signal) {
    const response = await fetchImpl(urls[name], {
      method: "POST",
      cache: "no-store",
      credentials: "include",
      headers: accountMutationHeaders(session),
      redirect: "error",
      signal,
      body: JSON.stringify(command),
    });
    if (response.status === 401) return Object.freeze({ kind: "signedOut" });
    if (![200, 409].includes(response.status)) {
      throw new Error("Tutorial progress mutation failed.");
    }
    const acknowledgement = decodeAcknowledgement(
      await readBoundedAccountJson(
        response,
        "Account tutorial progress acknowledgement",
        RESPONSE_MAX_BYTES,
      ),
      command.operationId,
    );
    if (
      (response.status === 200 && acknowledgement.kind !== "applied")
      || (response.status === 409 && acknowledgement.kind !== "conflict")
    ) {
      throw new TypeError("Tutorial progress status and acknowledgement disagree.");
    }
    return acknowledgement;
  }

  async function enable(session, initial, { signal } = {}) {
    return post(session, "enable", {
      schemaVersion: SCHEMA_VERSION,
      operationId: operationId(),
      initial: decodeTutorialProgressEnabledState(initial),
    }, signal);
  }

  async function complete(session, expectedRevision, completedIds, { signal } = {}) {
    return post(session, "complete", {
      schemaVersion: SCHEMA_VERSION,
      operationId: operationId(),
      expectedRevision: revisionNumber(expectedRevision),
      completedIds: exactCompletedIds(
        canonicalizeTutorialProgressCommandIds(completedIds),
        { allowEmpty: false },
      ),
    }, signal);
  }

  async function reset(session, expectedRevision, { signal } = {}) {
    return post(session, "reset", {
      schemaVersion: SCHEMA_VERSION,
      operationId: operationId(),
      expectedRevision: revisionNumber(expectedRevision),
    }, signal);
  }

  return Object.freeze({ load, enable, complete, reset });
}
